import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Chip, Divider, Pagination, FormControl, InputLabel,
  Select, MenuItem, Card, CardContent, Button, Autocomplete, Link,
  Tabs, Tab, ButtonGroup, Tooltip,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  OpenInNew as ExtIcon,
  BarChart as BarChartIcon,
  TableChart as TableIcon,
  PieChart as PieChartIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
  LineChart, Line,
} from 'recharts';
import { jiraAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtSeconds(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Format a local Date as YYYY-MM-DD without UTC shift (toISOString breaks IST etc.). */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOf(unit: 'week' | 'month' | 'year'): string {
  const d = new Date();
  if (unit === 'week') { const day = d.getDay(); d.setDate(d.getDate() - day + (day === 0 ? -6 : 1)); }
  if (unit === 'month') d.setDate(1);
  if (unit === 'year')  { d.setMonth(0); d.setDate(1); }
  return toLocalYmd(d);
}

function endOf(unit: 'week' | 'month' | 'year'): string {
  const d = new Date();
  if (unit === 'week') { const day = d.getDay(); d.setDate(d.getDate() - day + (day === 0 ? 0 : 7)); }
  if (unit === 'month') { d.setMonth(d.getMonth() + 1); d.setDate(0); }
  if (unit === 'year')  { d.setMonth(11); d.setDate(31); }
  return toLocalYmd(d);
}

function addPeriod(unit: 'week' | 'month' | 'year', offset: -1 | 0): { from: string; to: string } {
  if (offset === 0) return { from: startOf(unit), to: endOf(unit) };
  const d = new Date();
  if (unit === 'week') {
    const day = d.getDay();
    const thisMonday = new Date(d); thisMonday.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
    const lastSunday = new Date(lastMonday); lastSunday.setDate(lastMonday.getDate() + 6);
    return { from: toLocalYmd(lastMonday), to: toLocalYmd(lastSunday) };
  }
  if (unit === 'month') {
    const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const last  = new Date(d.getFullYear(), d.getMonth(), 0);
    return { from: toLocalYmd(first), to: toLocalYmd(last) };
  }
  // year
  const yr = d.getFullYear() - 1;
  return { from: `${yr}-01-01`, to: `${yr}-12-31` };
}

const PRESETS = [
  { label: 'This Week',   get: () => addPeriod('week',  0) },
  { label: 'Last Week',   get: () => addPeriod('week', -1) },
  { label: 'This Month',  get: () => addPeriod('month',  0) },
  { label: 'Last Month',  get: () => addPeriod('month', -1) },
  { label: 'This Year',   get: () => addPeriod('year',  0) },
  { label: 'Last Year',   get: () => addPeriod('year', -1) },
];

const PIE_COLORS = ['#1976d2','#2e7d32','#ed6c02','#9c27b0','#d32f2f','#0288d1','#388e3c','#f57c00'];

const JIRA_BASE_URL = 'https://appelsolutions.atlassian.net';

const ALL_PROJECTS_OPTION = { key: 'ALL', name: 'All Projects' };

// ─── custom recharts tooltip ─────────────────────────────────────────────────

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, fontSize: 13 }}>
      <Typography variant="caption" display="block" fontWeight={600}>{label}</Typography>
      <Typography variant="caption">{fmtSeconds(Math.round(payload[0].value * 3600))}</Typography>
    </Paper>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const JiraTimeLogs: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isPmpAdmin = user?.roles?.some((r: any) => r.slug === 'pmp_admin') ?? false;

  // JIRA projects list — All Projects is always available
  const [jiraProjects, setJiraProjects] = useState<{ key: string; name: string }[]>([ALL_PROJECTS_OPTION]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState('');
  const [selectedProject, setSelectedProject] = useState<{ key: string; name: string } | null>(ALL_PROJECTS_OPTION);

  // Filters — default: current month
  const [dateFrom, setDateFrom] = useState(() => addPeriod('month', 0).from);
  const [dateTo, setDateTo] = useState(() => addPeriod('month', 0).to);
  const [authorFilter, setAuthorFilter] = useState('');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  // Active tab
  const [tab, setTab] = useState(0);

  // ── chart data ──
  const [chartData, setChartData] = useState<any>(null);
  const [loadingChart, setLoadingChart] = useState(false);

  // ── summary ──
  const [summary, setSummary] = useState<any[]>([]);
  const [totalTime, setTotalTime] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);

  // ── logs table ──
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // ── fetch JIRA project list ──
  useEffect(() => {
    jiraAPI.listProjects()
      .then(r => setJiraProjects([ALL_PROJECTS_OPTION, ...(r.data.data || [])]))
      .catch(e => setProjectsError(e.response?.data?.message || 'Failed to load JIRA projects.'))
      .finally(() => setLoadingProjects(false));
  }, []);

  // ── data fetchers ──
  // Helper: resolve the project_key param — undefined means all
  const projectKeyParam = selectedProject?.key === 'ALL' ? undefined : selectedProject?.key;

  const fetchChart = useCallback(async () => {
    if (!selectedProject) return;
    setLoadingChart(true);
    try {
      const r = await jiraAPI.getChartData({
        project_key: projectKeyParam,
        date_from: dateFrom || undefined,
        date_to:   dateTo   || undefined,
        group_by:  groupBy,
      });
      setChartData(r.data.data);
    } catch { setChartData(null); }
    finally { setLoadingChart(false); }
  }, [selectedProject, projectKeyParam, dateFrom, dateTo, groupBy]);

  const fetchSummary = useCallback(async () => {
    if (!selectedProject) return;
    setLoadingSummary(true);
    try {
      const r = await jiraAPI.getSummary({
        project_key: projectKeyParam,
        date_from: dateFrom || undefined,
        date_to:   dateTo   || undefined,
      });
      setSummary(r.data.data || []);
      setTotalTime(r.data.total_time_formatted || '');
    } catch { setSummary([]); }
    finally { setLoadingSummary(false); }
  }, [selectedProject, projectKeyParam, dateFrom, dateTo]);

  const fetchLogs = useCallback(async (pg = page) => {
    if (!selectedProject) return;
    setLoadingLogs(true);
    setLogsError('');
    setHasSearched(true);
    try {
      const r = await jiraAPI.getTimeLogs({
        project_key: projectKeyParam,
        date_from: dateFrom || undefined,
        date_to:   dateTo   || undefined,
        author:    authorFilter || undefined,
        per_page:  perPage,
        page:      pg,
      });
      setLogs(r.data.data || []);
      setTotal(r.data.total || 0);
      setLastPage(r.data.last_page || 1);
    } catch (e: any) {
      setLogsError(e.response?.data?.message || 'Failed to fetch time logs.');
      setLogs([]);
    }
    finally { setLoadingLogs(false); }
  }, [selectedProject, projectKeyParam, dateFrom, dateTo, authorFilter, perPage, page]);

  // ── auto-load: All Projects + current month on first open ──
  useEffect(() => {
    fetchChart();
    fetchSummary();
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-fetch logs when page changes
  useEffect(() => {
    if (hasSearched && selectedProject) fetchLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ── CSV export ──
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!selectedProject) return;
    setExporting(true);
    try {
      const r = await jiraAPI.exportCsv({
        project_key: projectKeyParam,
        date_from: dateFrom || undefined,
        date_to:   dateTo   || undefined,
        author:    authorFilter || undefined,
      });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const label = selectedProject.key === 'ALL' ? 'all-projects' : selectedProject.key.toLowerCase();
      a.href     = url;
      a.download = `jira-timelogs-${label}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently ignore — could add a snackbar here
    } finally {
      setExporting(false);
    }
  };

  const handleApply = () => {
    setPage(1);
    fetchChart();
    fetchSummary();
    fetchLogs(1);
  };

  const handlePreset = (preset: typeof PRESETS[number]) => {
    const { from, to } = preset.get();
    setDateFrom(from);
    setDateTo(to);
  };

  const handleClear = () => {
    const { from, to } = addPeriod('month', 0);
    setDateFrom(from);
    setDateTo(to);
    setAuthorFilter('');
    setSelectedProject(ALL_PROJECTS_OPTION);
    setPage(1);
    setLogs([]);
    setSummary([]);
    setChartData(null);
    setTotal(0);
    setHasSearched(false);
  };

  const handleProjectChange = (_: any, val: typeof selectedProject) => {
    setSelectedProject(val);
    setLogs([]); setSummary([]); setChartData(null);
    setTotal(0); setHasSearched(false); setPage(1);
  };

  const isLoading = loadingChart || loadingSummary || loadingLogs;

  // ─── render ───────────────────────────────────────────────────────────────

  if (!isPmpAdmin) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">You do not have permission to view JIRA Time Logs.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimeIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>JIRA Time Logs</Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={() => navigate(-1)}>Back</Button>
      </Box>

      {/* ── Filter Panel ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {projectsError && <Alert severity="error" sx={{ mb: 2 }}>{projectsError}</Alert>}

        {/* project selector + date filters */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <Autocomplete
            sx={{ minWidth: 300, flex: '1 1 300px', maxWidth: 460 }}
            options={jiraProjects}
            loading={loadingProjects}
            getOptionLabel={(o) => `${o.key} — ${o.name}`}
            value={selectedProject}
            onChange={handleProjectChange}
            isOptionEqualToValue={(o, v) => o.key === v.key}
            renderInput={(params) => (
              <TextField
                {...params}
                label="JIRA Project"
                placeholder={loadingProjects ? 'Loading…' : 'Select a project'}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{loadingProjects && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                }}
              />
            )}
            renderOption={(props, o) => (
              <li {...props} key={o.key}>
                {o.key === 'ALL' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="ALL" size="small" color="primary" sx={{ fontWeight: 700 }} />
                    <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{o.key}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.name}</Typography>
                  </Box>
                )}
              </li>
            )}
            noOptionsText={loadingProjects ? 'Loading…' : 'No projects found'}
          />
          <TextField label="Date From" type="date" size="small" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 155 }} />
          <TextField label="Date To"   type="date" size="small" value={dateTo}
            onChange={e => setDateTo(e.target.value)}   InputLabelProps={{ shrink: true }} sx={{ minWidth: 155 }} />
          <TextField label="Author Email" size="small" placeholder="Filter by email" value={authorFilter}
            onChange={e => setAuthorFilter(e.target.value)} sx={{ minWidth: 200 }} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Group By</InputLabel>
            <Select value={groupBy} label="Group By" onChange={e => setGroupBy(e.target.value as any)}>
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Quick date presets */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>Quick:</Typography>
          {PRESETS.map(p => (
            <Chip key={p.label} label={p.label} size="small" variant="outlined" clickable
              onClick={() => handlePreset(p)}
              color={dateFrom === p.get().from && dateTo === p.get().to ? 'primary' : 'default'}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={handleApply} disabled={!selectedProject || isLoading}>
            {isLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Load Report'}
          </Button>
          <Button variant="outlined" onClick={handleClear} disabled={isLoading}>Clear</Button>
          {hasSearched && (
            <Button
              variant="outlined"
              color="success"
              startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting || !selectedProject}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* ── Results ── */}
      {hasSearched && (
        <>
          {/* ── KPI strip ── */}
          {!loadingSummary && summary.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Card variant="outlined" sx={{ flex: '1 1 140px' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="caption" color="text.secondary">Total Time</Typography>
                  <Typography variant="h5" color="primary.main" fontWeight={700}>{totalTime}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: '1 1 140px' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="caption" color="text.secondary">Contributors</Typography>
                  <Typography variant="h5" fontWeight={700}>{summary.length}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: '1 1 140px' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="caption" color="text.secondary">Log Entries</Typography>
                  <Typography variant="h5" fontWeight={700}>{total}</Typography>
                </CardContent>
              </Card>
              {chartData?.by_ticket && (
                <Card variant="outlined" sx={{ flex: '1 1 140px' }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary">Tickets Logged</Typography>
                    <Typography variant="h5" fontWeight={700}>{chartData.by_ticket.length}</Typography>
                  </CardContent>
                </Card>
              )}
              {chartData?.overall_avg_per_working_day && (
                <Card variant="outlined" sx={{ flex: '1 1 180px', borderColor: 'success.main' }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary">
                      Avg / Working Day
                    </Typography>
                    <Typography variant="h5" color="success.main" fontWeight={700}>
                      {chartData.overall_avg_per_working_day.avg_formatted_per_day}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {chartData.overall_avg_per_working_day.working_days} working days (Mon–Fri)
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}

          {/* ── Tabs ── */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab icon={<BarChartIcon fontSize="small" />} iconPosition="start" label="Charts" />
              <Tab icon={<PieChartIcon fontSize="small" />} iconPosition="start" label="By Author" />
              <Tab icon={<BarChartIcon fontSize="small" />} iconPosition="start" label="By Ticket" />
              <Tab icon={<TableIcon fontSize="small" />}   iconPosition="start" label="All Logs" />
            </Tabs>

            <Box sx={{ p: 3 }}>

              {/* ── Tab 0: Charts ── */}
              {tab === 0 && (
                loadingChart ? (
                  <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                ) : chartData?.timeline?.length ? (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Time Logged — by {groupBy === 'day' ? 'Day' : groupBy === 'week' ? 'Week' : 'Month'}
                    </Typography>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={chartData.timeline} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" angle={-40} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                        <YAxis tickFormatter={v => `${v}h`} tick={{ fontSize: 11 }} />
                        <RTooltip content={<CustomBarTooltip />} />
                        <Bar dataKey="hours" fill="#1976d2" radius={[4, 4, 0, 0]} name="Hours" />
                      </BarChart>
                    </ResponsiveContainer>

                    {chartData.timeline.length >= 4 && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Trend</Typography>
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={chartData.timeline} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-40} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                            <YAxis tickFormatter={v => `${v}h`} tick={{ fontSize: 11 }} />
                            <RTooltip content={<CustomBarTooltip />} />
                            <Line type="monotone" dataKey="hours" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </>
                    )}

                    {/* Monthly avg per working day (Mon–Fri) */}
                    {chartData.monthly_averages?.length > 0 && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                          Monthly Average per Working Day
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          Total logged time ÷ working days in the month (Saturday and Sunday excluded). Current month counts days up to today.
                        </Typography>

                        <ResponsiveContainer width="100%" height={Math.max(220, chartData.monthly_averages.length * 40)}>
                          <BarChart
                            data={chartData.monthly_averages}
                            margin={{ top: 5, right: 20, left: 0, bottom: 40 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month_label" angle={-30} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                            <YAxis tickFormatter={v => `${v}h`} tick={{ fontSize: 11 }} />
                            <RTooltip
                              formatter={(v: any) => [`${Number(v).toFixed(2)}h`, 'Avg / working day']}
                              labelFormatter={(label: any) => String(label)}
                            />
                            <Bar dataKey="avg_hours_per_day" fill="#2e7d32" radius={[4, 4, 0, 0]} name="Avg hours / day" />
                          </BarChart>
                        </ResponsiveContainer>

                        <TableContainer sx={{ mt: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Month</TableCell>
                                <TableCell align="right">Total Time</TableCell>
                                <TableCell align="right">Working Days</TableCell>
                                <TableCell align="right">Avg / Working Day</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {chartData.monthly_averages.map((m: any) => (
                                <TableRow key={m.month} hover>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{m.month_label}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip label={m.total_formatted} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">{m.working_days}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip label={m.avg_formatted_per_day} size="small" color="success" variant="outlined" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Box>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No chart data available for the selected filters.
                  </Typography>
                )
              )}

              {/* ── Tab 1: By Author ── */}
              {tab === 1 && (
                loadingSummary ? (
                  <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                ) : summary.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {/* Pie chart */}
                    <Box sx={{ flex: '1 1 280px', minWidth: 240 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Distribution</Typography>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={summary}
                            dataKey="total_seconds"
                            nameKey="author_name"
                            cx="50%" cy="50%"
                            outerRadius={90}
                            label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                            labelLine={false}
                          >
                            {summary.map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Legend />
                          <RTooltip formatter={(v: any, name: any) => [fmtSeconds(v), name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Author cards */}
                    <Box sx={{ flex: '2 1 340px' }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Breakdown</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Author</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell align="right">Total Time</TableCell>
                              <TableCell align="right">Entries</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.map((s: any, i: number) => (
                              <TableRow key={s.author_email || s.author_name} hover>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                                    <Typography variant="body2">{s.author_name}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell><Typography variant="caption" color="text.secondary">{s.author_email}</Typography></TableCell>
                                <TableCell align="right"><Chip label={s.total_time_formatted} size="small" color="primary" variant="outlined" /></TableCell>
                                <TableCell align="right"><Typography variant="body2">{s.worklog_count}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={4}>No author data.</Typography>
                )
              )}

              {/* ── Tab 2: By Ticket ── */}
              {tab === 2 && (
                loadingChart ? (
                  <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                ) : chartData?.by_ticket?.length ? (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Top Tickets by Time Logged</Typography>
                    <ResponsiveContainer width="100%" height={Math.max(280, chartData.by_ticket.length * 34)}>
                      <BarChart
                        layout="vertical"
                        data={chartData.by_ticket.slice(0, 15)}
                        margin={{ top: 0, right: 60, left: 20, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={v => `${v}h`} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="ticket" tick={{ fontSize: 11 }} width={70} />
                        <RTooltip
                          formatter={(v: any, _: any, props: any) => [
                            fmtSeconds(Math.round(v * 3600)),
                            props.payload.summary,
                          ]}
                        />
                        <Bar dataKey="hours" fill="#ed6c02" radius={[0, 4, 4, 0]} name="Hours" />
                      </BarChart>
                    </ResponsiveContainer>

                    <Divider sx={{ my: 3 }} />
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Ticket</TableCell>
                            <TableCell>Summary</TableCell>
                            <TableCell align="right">Time Logged</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {chartData.by_ticket.map((t: any) => (
                            <TableRow key={t.ticket} hover>
                              <TableCell>
                                <Link href={`${JIRA_BASE_URL}/browse/${t.ticket}`} target="_blank" rel="noopener noreferrer"
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                                  {t.ticket}<ExtIcon sx={{ fontSize: 12 }} />
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 360 }} noWrap title={t.summary}>{t.summary}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={t.formatted} size="small" color="warning" variant="outlined" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={4}>No ticket data.</Typography>
                )
              )}

              {/* ── Tab 3: All Logs ── */}
              {tab === 3 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      All Entries
                      {total > 0 && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>({total} total)</Typography>}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel>Per Page</InputLabel>
                      <Select value={perPage} label="Per Page" onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {logsError && <Alert severity="error" sx={{ mb: 2 }}>{logsError}</Alert>}

                  {loadingLogs ? (
                    <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
                  ) : (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Ticket</TableCell>
                              <TableCell>Summary</TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Status</TableCell>
                              <TableCell>Author</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Time</TableCell>
                              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Comment</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {logs.map((log: any, idx: number) => (
                              <TableRow key={`${log.worklog_id}-${idx}`} hover>
                                <TableCell>
                                  <Link href={`${JIRA_BASE_URL}/browse/${log.issue_key}`} target="_blank" rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                                    {log.issue_key}<ExtIcon sx={{ fontSize: 12 }} />
                                  </Link>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  <Typography variant="body2" noWrap title={log.issue_summary}>{log.issue_summary}</Typography>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                  <Chip label={log.issue_type} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                  <Chip label={log.issue_status} size="small" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap>{log.author_name}</Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" noWrap>{log.author_email}</Typography>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(log.started)}</TableCell>
                                <TableCell align="right">
                                  <Chip label={log.time_spent || fmtSeconds(log.time_spent_seconds)} size="small" color="primary" variant="outlined" />
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, maxWidth: 180 }}>
                                  <Typography variant="caption" color="text.secondary" noWrap title={log.comment}>{log.comment || '—'}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                            {logs.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={8} align="center">
                                  <Typography variant="body2" color="text.secondary" py={3}>No time logs found.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {total > perPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Pagination count={lastPage} page={page} onChange={(_, v) => setPage(v)}
                            color="primary" showFirstButton showLastButton />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default JiraTimeLogs;
